import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { getBranding, subscribeBranding } from "../lib/branding";
import { getAuth } from "../lib/auth";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Cell,
} from "recharts";
import {
  Activity,
  ArrowLeft,
  Banknote,
  Clock,
  Crown,
  Flame,
  Receipt,
  TrendingUp,
  Trophy,
} from "lucide-react";

const REFRESH_MS = 5000;

const STATUS_LABEL = {
  maken: "MAKEN",
  klaar: "KLAAR",
  afgerond: "AFGEROND",
};

const STATUS_COLOR = {
  maken: "#FF8A00",
  klaar: "#0E8F2A",
  afgerond: "#888",
};

const BETAAL_LABEL = {
  contant: "Contant",
  pin: "Pin",
  pinautomaat: "Pinautomaat",
  "tap-to-pay": "Tap-to-pay",
  stripe: "Stripe",
};

function fmtEUR(n) {
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(n || 0);
}

function fmtTime(d) {
  return d.toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function fmtTimeShort(iso) {
  try {
    return new Date(iso).toLocaleTimeString("nl-NL", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function Live() {
  const [data, setData] = useState(null);
  const [now, setNow] = useState(new Date());
  const [brand, setBrand] = useState(getBranding());
  const [error, setError] = useState(null);
  const nav = useNavigate();

  // Admin-only guard — redirect naar login als geen admin sessie
  useEffect(() => {
    const auth = getAuth();
    if (!auth || auth.role !== "admin") {
      nav("/", { replace: true });
    }
  }, [nav]);

  useEffect(() => {
    const unsub = subscribeBranding(setBrand);
    return unsub;
  }, []);

  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (document.hidden) return;
      try {
        const { data } = await api.get("/dashboard/live");
        if (alive) {
          setData(data);
          setError(null);
        }
      } catch (e) {
        if (alive) setError("Verbinding verloren");
      }
    };
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  const maxBucket = useMemo(() => {
    if (!data?.orders_per_kwartier) return 0;
    return Math.max(1, ...data.orders_per_kwartier.map((b) => b.orders));
  }, [data]);

  const totalActief = data?.actief_nu || 0;
  const drukste = data?.drukste_team;

  return (
    <div
      data-testid="live-dashboard"
      className="min-h-screen w-full overflow-hidden text-white"
      style={{
        background:
          "radial-gradient(1200px 600px at 10% -10%, #1a3a25 0%, transparent 60%), radial-gradient(1000px 600px at 100% 110%, #2a1a3a 0%, transparent 55%), #0b0f17",
        fontFamily: "var(--font-body)",
      }}
    >
      {/* Top bar */}
      <div className="px-4 md:px-8 py-4 md:py-5 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <button
            onClick={() => nav("/")}
            className="btn-press p-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10"
            title="Terug"
            data-testid="live-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          {brand.logo_url ? (
            <img src={brand.logo_url} alt="logo" className="h-10 object-contain" />
          ) : null}
          <div>
            <div className="text-2xl font-extrabold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              {brand.bedrijfsnaam || "QDB AI SYSTEEM"}
            </div>
            <div className="text-xs uppercase tracking-[0.25em] text-white/50">
              Live Festival Dashboard
            </div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <LivePulse error={error} />
          <div className="text-right">
            <div className="text-3xl font-extrabold tabular-nums" style={{ fontFamily: "var(--font-display)" }}>
              {fmtTime(now)}
            </div>
            <div className="text-xs uppercase tracking-widest text-white/50">
              {now.toLocaleDateString("nl-NL", {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}
            </div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="px-4 md:px-8 pt-4 md:pt-6 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
        <KpiCard
          testid="kpi-omzet"
          icon={Banknote}
          label="Omzet vandaag"
          value={fmtEUR(data?.omzet_vandaag || 0)}
          accent="#22c55e"
        />
        <KpiCard
          testid="kpi-orders"
          icon={Receipt}
          label="Bestellingen"
          value={(data?.aantal_orders ?? 0).toString()}
          accent="#facc15"
        />
        <KpiCard
          testid="kpi-gem"
          icon={TrendingUp}
          label="Gem. orderwaarde"
          value={fmtEUR(data?.gemiddelde_orderwaarde || 0)}
          accent="#38bdf8"
        />
        <KpiCard
          testid="kpi-actief"
          icon={Activity}
          label="Nu in voorbereiding"
          value={totalActief.toString()}
          accent="#fb923c"
          pulse={totalActief > 0}
        />
      </div>

      {/* Main grid */}
      <div className="px-4 md:px-8 pt-4 md:pt-6 pb-6 md:pb-8 grid grid-cols-1 xl:grid-cols-3 gap-3 md:gap-5">
        {/* Left: chart */}
        <div
          className="xl:col-span-2 rounded-3xl p-6 border border-white/10"
          style={{ background: "rgba(255,255,255,0.03)", backdropFilter: "blur(8px)" }}
          data-testid="live-chart-card"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-lg font-extrabold inline-flex items-center gap-2" style={{ fontFamily: "var(--font-display)" }}>
                <Clock className="w-5 h-5" /> Bestellingen per kwartier
              </div>
              <div className="text-xs uppercase tracking-widest text-white/50">
                Laatste 4 uur
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-white/50">Piek</div>
              <div className="text-2xl font-extrabold tabular-nums">{maxBucket}</div>
            </div>
          </div>

          <div style={{ width: "100%", height: 240, minHeight: 240 }}>
            <ResponsiveContainer width="100%" height="100%" minHeight={240}>
              <BarChart data={data?.orders_per_kwartier || []} margin={{ top: 10, right: 8, left: -16, bottom: 0 }}>
                <XAxis
                  dataKey="label"
                  tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                  interval={1}
                />
                <YAxis
                  tick={{ fill: "rgba(255,255,255,0.6)", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={{ fill: "rgba(255,255,255,0.05)" }}
                  contentStyle={{
                    background: "#0b0f17",
                    border: "1px solid rgba(255,255,255,0.15)",
                    borderRadius: 12,
                    color: "#fff",
                  }}
                  formatter={(value, name, props) => {
                    if (name === "orders") return [value, "Bestellingen"];
                    return [value, name];
                  }}
                />
                <Bar dataKey="orders" radius={[8, 8, 0, 0]}>
                  {(data?.orders_per_kwartier || []).map((b, i) => (
                    <Cell
                      key={i}
                      fill={
                        b.orders === maxBucket && maxBucket > 0
                          ? brand.hoofdkleur || "#0E8F2A"
                          : "rgba(255,255,255,0.25)"
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Right: top products */}
        <div
          className="rounded-3xl p-6 border border-white/10"
          style={{ background: "rgba(255,255,255,0.03)", backdropFilter: "blur(8px)" }}
          data-testid="live-top-products"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="text-lg font-extrabold inline-flex items-center gap-2" style={{ fontFamily: "var(--font-display)" }}>
              <Trophy className="w-5 h-5" /> Topverkopers
            </div>
            <div className="text-xs uppercase tracking-widest text-white/50">Vandaag</div>
          </div>
          {data?.top_producten?.length ? (
            <ol className="space-y-3">
              {data.top_producten.map((p, i) => (
                <li
                  key={p.naam + i}
                  className="flex items-center gap-3 px-3 py-3 rounded-2xl border border-white/5"
                  style={{ background: i === 0 ? "rgba(244,196,0,0.08)" : "rgba(255,255,255,0.03)" }}
                  data-testid={`top-prod-${i}`}
                >
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center font-extrabold tabular-nums"
                    style={{
                      background: i === 0 ? "#f4c400" : "rgba(255,255,255,0.08)",
                      color: i === 0 ? "#1a1a1a" : "#fff",
                    }}
                  >
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{p.naam}</div>
                    <div className="text-xs text-white/50">
                      {p.aantal}× · {fmtEUR(p.omzet)}
                    </div>
                  </div>
                  {i === 0 && <Crown className="w-5 h-5 text-yellow-400" />}
                </li>
              ))}
            </ol>
          ) : (
            <div className="text-white/40 text-sm py-8 text-center" data-testid="top-prod-empty">
              Nog geen verkopen vandaag
            </div>
          )}
        </div>

        {/* Teams */}
        <div
          className="xl:col-span-2 rounded-3xl p-6 border border-white/10"
          style={{ background: "rgba(255,255,255,0.03)", backdropFilter: "blur(8px)" }}
          data-testid="live-team-grid"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="text-lg font-extrabold inline-flex items-center gap-2" style={{ fontFamily: "var(--font-display)" }}>
              <Flame className="w-5 h-5" /> Teams
            </div>
            {drukste && drukste.orders > 0 && (
              <div className="text-xs uppercase tracking-widest text-white/50 inline-flex items-center gap-2">
                Drukste: <span className="text-white font-bold">{drukste.letter}</span>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(data?.per_team || []).map((t) => (
              <div
                key={t.letter}
                className="rounded-2xl p-5 border-2 relative overflow-hidden"
                style={{
                  borderColor: t.kleur,
                  background: `linear-gradient(135deg, ${t.kleur}22 0%, rgba(255,255,255,0.02) 80%)`,
                }}
                data-testid={`live-team-${t.letter}`}
              >
                <div
                  className="absolute -top-6 -right-6 w-24 h-24 rounded-full opacity-30 blur-2xl"
                  style={{ background: t.kleur }}
                />
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl font-extrabold"
                    style={{ background: t.kleur, color: "#fff" }}
                  >
                    {t.letter}
                  </div>
                  <div>
                    <div className="font-extrabold">{t.naam}</div>
                    <div className="text-xs text-white/50">
                      {t.actief_nu > 0 ? `${t.actief_nu} actief nu` : "Rustig"}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-xl bg-white/5 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Orders</div>
                    <div className="text-2xl font-extrabold tabular-nums">{t.orders}</div>
                  </div>
                  <div className="rounded-xl bg-white/5 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Omzet</div>
                    <div className="text-2xl font-extrabold tabular-nums">{fmtEUR(t.omzet)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recente orders */}
        <div
          className="rounded-3xl p-6 border border-white/10"
          style={{ background: "rgba(255,255,255,0.03)", backdropFilter: "blur(8px)" }}
          data-testid="live-recent-orders"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="text-lg font-extrabold inline-flex items-center gap-2" style={{ fontFamily: "var(--font-display)" }}>
              <Receipt className="w-5 h-5" /> Recente bestellingen
            </div>
          </div>
          {data?.recente_orders?.length ? (
            <ul className="space-y-2">
              {data.recente_orders.map((o) => (
                <li
                  key={o.ordernummer + o.created_at}
                  className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl border border-white/5 bg-white/[0.03]"
                  data-testid={`recent-${o.ordernummer}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="font-extrabold rounded-lg px-2 py-1 text-sm tabular-nums"
                      style={{ background: o.team_kleur, color: "#fff" }}
                    >
                      {o.ordernummer}
                    </div>
                    <div className="text-xs text-white/60 truncate">
                      {o.aantal_items}× · {fmtTimeShort(o.created_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-sm font-bold tabular-nums">{fmtEUR(o.totaal)}</span>
                    <span
                      className="text-[9px] font-extrabold px-2 py-1 rounded uppercase tracking-widest"
                      style={{ background: STATUS_COLOR[o.status] || "#444", color: "#fff" }}
                    >
                      {STATUS_LABEL[o.status] || o.status}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-white/40 text-sm py-8 text-center" data-testid="recent-empty">
              Nog geen bestellingen vandaag
            </div>
          )}
        </div>

        {/* Payment methods bar */}
        {data?.per_betaalmethode?.length > 0 && (
          <div
            className="xl:col-span-3 rounded-3xl p-5 border border-white/10 flex flex-wrap items-center gap-3"
            style={{ background: "rgba(255,255,255,0.03)" }}
            data-testid="live-betaal-row"
          >
            <div className="text-xs uppercase tracking-widest text-white/50 mr-2">Betaalmethoden</div>
            {data.per_betaalmethode.map((b) => (
              <div
                key={b.naam}
                className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm font-bold inline-flex items-center gap-2"
                data-testid={`betaal-${b.naam}`}
              >
                <span>{BETAAL_LABEL[b.naam] || b.naam}</span>
                <span className="text-white/50 tabular-nums">{b.aantal}×</span>
                {typeof b.omzet === "number" && (
                  <span className="text-green-300/80 tabular-nums">{fmtEUR(b.omzet)}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, accent, pulse, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-3xl p-5 border border-white/10 relative overflow-hidden"
      style={{
        background: "rgba(255,255,255,0.03)",
        backdropFilter: "blur(8px)",
      }}
    >
      <div
        className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-3xl opacity-25"
        style={{ background: accent }}
      />
      <div className="flex items-center gap-2 mb-2 text-white/50 text-xs uppercase tracking-[0.2em]">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        {label}
      </div>
      <div
        className="text-4xl xl:text-5xl font-extrabold tabular-nums leading-none"
        style={{ fontFamily: "var(--font-display)" }}
      >
        {value}
      </div>
      {pulse && (
        <div className="mt-3 inline-flex items-center gap-2 text-xs text-white/70">
          <span className="relative inline-flex">
            <span className="w-2 h-2 rounded-full" style={{ background: accent }} />
            <span
              className="absolute inset-0 w-2 h-2 rounded-full animate-ping"
              style={{ background: accent }}
            />
          </span>
          live
        </div>
      )}
    </div>
  );
}

function LivePulse({ error }) {
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5">
      <span className="relative inline-flex">
        <span
          className={`w-2 h-2 rounded-full ${error ? "bg-red-400" : "bg-green-400"}`}
        />
        {!error && (
          <span className="absolute inset-0 w-2 h-2 rounded-full bg-green-400 animate-ping" />
        )}
      </span>
      <span className="text-xs uppercase tracking-widest text-white/70">
        {error ? "Offline" : "Live"}
      </span>
    </div>
  );
}
